import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import {
  listVeriffWebhookLog,
  getVeriffSessionContext,
  type VeriffLogRow,
  type VeriffSessionContext,
} from "@/lib/veriff-log.functions";

function downloadJson(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function SessionContext({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["veriff-session-context", sessionId],
    queryFn: () => getVeriffSessionContext({ data: { session_id: sessionId } }),
  });
  const ctx = data as (VeriffSessionContext & { error?: string }) | undefined;
  if (isLoading) return <p className="mt-2 text-xs text-muted-foreground">Loading session…</p>;
  if (!ctx || ctx.error)
    return <p className="mt-2 text-xs text-destructive">{ctx?.error ?? "Unavailable"}</p>;
  return (
    <div className="mt-2 border border-border bg-background p-3 text-[11px]">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
        <div><dt className="text-muted-foreground">Current status</dt><dd>{ctx.current_status ?? "—"}</dd></div>
        <div><dt className="text-muted-foreground">Submitted</dt><dd>{ctx.submitted_at ? new Date(ctx.submitted_at).toLocaleString("en-GB") : "—"}</dd></div>
        <div><dt className="text-muted-foreground">Reviewed</dt><dd>{ctx.reviewed_at ? new Date(ctx.reviewed_at).toLocaleString("en-GB") : "—"}</dd></div>
        <div><dt className="text-muted-foreground">Expires</dt><dd>{ctx.expires_at ? new Date(ctx.expires_at).toLocaleString("en-GB") : "—"}</dd></div>
        <div><dt className="text-muted-foreground">Deliveries</dt><dd>{ctx.deliveries.length}</dd></div>
        <div><dt className="text-muted-foreground">Signature failures</dt><dd>{ctx.deliveries.filter((d) => !d.signature_valid).length}</dd></div>
      </dl>
      {ctx.notes && <p className="mt-2">{ctx.notes}</p>}
      <ul className="mt-3 space-y-1">
        {ctx.deliveries.map((d) => (
          <li key={d.id} className="flex flex-wrap gap-2">
            <span className="text-muted-foreground">{new Date(d.received_at).toLocaleString("en-GB")}</span>
            <span>{d.outcome.replace(/_/g, " ")}</span>
            <span>{d.raw_status ?? "—"} → {d.mapped_status ?? "—"}</span>
            <span>{d.signature_valid ? "signature ok" : "signature failed"}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() =>
          downloadJson(`veriff-session-${ctx.session_id}.json`, JSON.stringify(ctx, null, 2))
        }
        className="mt-3 underline underline-offset-4"
      >
        Download full session context
      </button>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/veriff-log")({
  head: () => ({
    meta: [
      { title: "Veriff webhook audit log — Admin" },
      {
        name: "description",
        content:
          "Audit trail of Veriff identity-check deliveries: decision payloads, timestamps and signature validation results.",
      },
    ],
  }),
  component: VeriffLogPage,
});

const OUTCOMES = [
  "all",
  "accepted",
  "duplicate",
  "queued_for_review",
  "invalid_signature",
  "missing_signature",
  "stale_timestamp",
  "rate_limited",
  "invalid_json",
  "not_configured",
  "error",
];

function tone(outcome: string) {
  if (outcome === "accepted") return "text-emerald-400 border-emerald-400/40";
  if (outcome === "duplicate" || outcome === "queued_for_review")
    return "text-amber-400 border-amber-400/40";
  return "text-destructive border-destructive/40";
}

function VeriffLogPage() {
  const [outcome, setOutcome] = useState("all");
  const [open, setOpen] = useState<string | null>(null);
  const [openSession, setOpenSession] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["veriff-webhook-log", outcome],
    queryFn: () => listVeriffWebhookLog({ data: { outcome, limit: 100 } }),
    refetchInterval: 30_000,
  });

  const result = data as { entries?: VeriffLogRow[]; error?: string } | undefined;
  const entries: VeriffLogRow[] = result?.entries ?? [];
  const error = result?.error ?? null;

  return (
    <main className="mx-auto max-w-5xl px-6 py-10">
      <BackToDashboard className="mb-4" />
      <h1 className="font-display text-3xl">Veriff audit log</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Every Right-to-Work session event and webhook delivery, with the decision payload,
        timestamp and signature validation result.
      </p>

      <label className="mt-6 block text-xs">
        <span className="mb-1 block uppercase tracking-wide-luxury text-muted-foreground">
          Filter by outcome
        </span>
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="border border-border bg-background px-3 py-2 text-sm"
        >
          {OUTCOMES.map((o) => (
            <option key={o} value={o}>
              {o.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>

      {error && <p className="mt-6 text-sm text-destructive">{error}</p>}
      {isLoading && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && entries.length === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">No deliveries recorded yet.</p>
      )}

      <ul className="mt-6 space-y-3">
        {entries.map((e) => (
          <li key={e.id} className="border border-border bg-card p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-wide-luxury ${tone(e.outcome)}`}>
                {e.outcome.replace(/_/g, " ")}
              </span>
              <span
                className={`border px-2 py-0.5 text-[10px] uppercase tracking-wide-luxury ${
                  e.signature_valid
                    ? "text-emerald-400 border-emerald-400/40"
                    : "text-destructive border-destructive/40"
                }`}
              >
                {e.signature_valid ? "signature ok" : "signature failed"}
              </span>
              <span className="text-xs text-muted-foreground">
                {new Date(e.received_at).toLocaleString("en-GB")}
              </span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Bartender</dt>
                <dd>{e.bartender_name ?? e.bartender_id ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Session</dt>
                <dd className="truncate">{e.session_id ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Reported status</dt>
                <dd>{e.raw_status ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Mapped status</dt>
                <dd>{e.mapped_status ?? "—"}</dd>
              </div>
            </dl>
            {e.reason && <p className="mt-2 text-xs text-foreground/80">{e.reason}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
              {e.payload != null && (
                <button
                  type="button"
                  onClick={() => setOpen(open === e.id ? null : e.id)}
                  className="underline underline-offset-4"
                >
                  {open === e.id ? "Hide payload" : "View decision payload"}
                </button>
              )}
              {e.payload != null && (
                <button
                  type="button"
                  onClick={() =>
                    downloadJson(
                      `veriff-payload-${e.event_id ?? e.id}.json`,
                      e.payload as string,
                    )
                  }
                  className="underline underline-offset-4"
                >
                  Download payload
                </button>
              )}
              {e.session_id && (
                <button
                  type="button"
                  onClick={() => setOpenSession(openSession === e.id ? null : e.id)}
                  className="underline underline-offset-4"
                >
                  {openSession === e.id ? "Hide session context" : "View session context"}
                </button>
              )}
            </div>
            {e.session_id && openSession === e.id && <SessionContext sessionId={e.session_id} />}
            {e.payload != null && (
              <>
                {open === e.id && (
                  <pre className="mt-2 max-h-72 overflow-auto border border-border bg-background p-3 text-[11px]">
                    {e.payload}
                  </pre>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
